import React, { useState, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { adminAPI } from "../services/api";
import {
  Key,
  Search,
  RefreshCw,
  XCircle,
  CheckCircle,
  AlertTriangle,
  ShieldOff,
  ChevronLeft,
  ChevronRight,
  Users,
  User,
} from "lucide-react";

const formatDate = (iso) =>
  iso
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(iso))
    : "—";

const TokenStatusBadge = ({ token }) => {
  const { t } = useTranslation();
  if (!token.isActive)
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-300">
        <XCircle className="h-3 w-3" />
        {t("adminTokens.statusRevoked")}
      </span>
    );
  if (token.expiresAt && new Date(token.expiresAt) < new Date())
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-300">
        <AlertTriangle className="h-3 w-3" />
        {t("adminTokens.statusExpired")}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-300">
      <CheckCircle className="h-3 w-3" />
      {t("adminTokens.statusActive")}
    </span>
  );
};

const MODE_ALL = "all";
const MODE_USER = "user";

export default function AdminTokenManagement() {
  const { t } = useTranslation();

  const [mode, setMode] = useState(MODE_ALL);

  // all-tokens mode
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [allResult, setAllResult] = useState(null);
  const [allLoading, setAllLoading] = useState(false);

  // by-user mode
  const [email, setEmail] = useState("");
  const [userResult, setUserResult] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  // shared
  const [revoking, setRevoking] = useState(null);

  // fetch all tokens
  const fetchAll = useCallback(async (currentPage, currentSearch) => {
    setAllLoading(true);
    try {
      const data = await adminAPI.listAllTokens({
        search: currentSearch,
        page: currentPage,
        limit: 50,
      });
      setAllResult(data);
    } catch {
      // handled by interceptor
    } finally {
      setAllLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === MODE_ALL) {
      fetchAll(page, search);
    }
  }, [mode, page, search, fetchAll]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  // fetch by user
  const handleUserSearch = useCallback(
    async (e) => {
      e.preventDefault();
      const trimmed = email.trim();
      if (!trimmed) return;
      setUserLoading(true);
      setUserResult(null);
      try {
        const data = await adminAPI.listUserTokens(trimmed);
        setUserResult(data);
      } catch {
        // handled by interceptor
      } finally {
        setUserLoading(false);
      }
    },
    [email],
  );

  // revoke (works in both modes)
  const handleRevoke = useCallback(
    async (tokenId) => {
      setRevoking(tokenId);
      try {
        await adminAPI.revokeUserToken(tokenId);
        toast.success(t("adminTokens.revokeSuccess"));
        const patch = (tokens) =>
          tokens.map((tk) =>
            tk.id === tokenId ? { ...tk, isActive: false } : tk,
          );
        if (mode === MODE_ALL) {
          setAllResult((prev) =>
            prev ? { ...prev, tokens: patch(prev.tokens) } : prev,
          );
        } else {
          setUserResult((prev) =>
            prev ? { ...prev, tokens: patch(prev.tokens) } : prev,
          );
        }
      } catch {
        // handled by interceptor
      } finally {
        setRevoking(null);
      }
    },
    [mode, t],
  );

  // shared table component
  const TokenTable = ({ tokens, showUser, loading }) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          {t("common.loading")}
        </div>
      );
    }
    if (!tokens || tokens.length === 0) {
      return (
        <div className="py-16 text-center text-sm text-slate-400">
          {t("adminTokens.noTokens")}
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5 text-xs font-medium uppercase tracking-wider text-slate-500">
              {showUser && (
                <th className="px-6 py-3 text-left">{t("adminTokens.colUser")}</th>
              )}
              <th className="px-6 py-3 text-left">{t("adminTokens.colName")}</th>
              <th className="px-6 py-3 text-left">{t("adminTokens.colPrefix")}</th>
              <th className="px-6 py-3 text-left">{t("adminTokens.colStatus")}</th>
              <th className="px-6 py-3 text-left">{t("adminTokens.colLastUsed")}</th>
              <th className="px-6 py-3 text-left">{t("adminTokens.colExpiry")}</th>
              <th className="px-6 py-3 text-left">{t("adminTokens.colUsage")}</th>
              <th className="px-6 py-3 text-left">{t("adminTokens.colCreated")}</th>
              <th className="px-6 py-3 text-right">{t("adminTokens.colActions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {tokens.map((tk) => (
              <tr
                key={tk.id}
                className="text-slate-300 transition hover:bg-slate-800/30"
              >
                {showUser && (
                  <td className="px-6 py-4 text-xs">
                    {tk.user ? (
                      <span className="font-mono text-cyan-300">
                        {tk.user.email}
                      </span>
                    ) : (
                      <span className="italic text-slate-500">
                        {t("adminTokens.noUser")}
                      </span>
                    )}
                  </td>
                )}
                <td className="px-6 py-4 font-medium text-white">{tk.name}</td>
                <td className="px-6 py-4 font-mono text-xs text-cyan-300">
                  {tk.prefix}…
                </td>
                <td className="px-6 py-4">
                  <TokenStatusBadge token={tk} />
                </td>
                <td className="px-6 py-4 text-xs text-slate-400">
                  {formatDate(tk.lastUsedAt)}
                </td>
                <td className="px-6 py-4 text-xs text-slate-400">
                  {formatDate(tk.expiresAt)}
                </td>
                <td className="px-6 py-4 text-xs text-slate-400">
                  {tk.usageCount}
                </td>
                <td className="px-6 py-4 text-xs text-slate-400">
                  {formatDate(tk.createdAt)}
                </td>
                <td className="px-6 py-4 text-right">
                  {tk.isActive && (
                    <button
                      type="button"
                      onClick={() => handleRevoke(tk.id)}
                      disabled={revoking === tk.id}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-300 transition hover:bg-red-500/20 disabled:opacity-50"
                    >
                      {revoking === tk.id ? (
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ShieldOff className="h-3.5 w-3.5" />
                      )}
                      {t("adminTokens.revoke")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const pagination = allResult?.pagination;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-white">
          {t("adminTokens.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          {t("adminTokens.subtitle")}
        </p>
      </div>

      {/* Mode tabs */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode(MODE_ALL)}
          className={`flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-medium transition ${
            mode === MODE_ALL
              ? "bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 text-white shadow-lg shadow-cyan-700/30"
              : "border border-slate-700 bg-slate-800/60 text-slate-300 hover:bg-slate-700"
          }`}
        >
          <Users className="h-4 w-4" />
          {t("adminTokens.modeAll")}
        </button>
        <button
          type="button"
          onClick={() => setMode(MODE_USER)}
          className={`flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-medium transition ${
            mode === MODE_USER
              ? "bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 text-white shadow-lg shadow-cyan-700/30"
              : "border border-slate-700 bg-slate-800/60 text-slate-300 hover:bg-slate-700"
          }`}
        >
          <User className="h-4 w-4" />
          {t("adminTokens.modeByUser")}
        </button>
      </div>

      {/* ALL TOKENS mode */}
      {mode === MODE_ALL && (
        <>
          <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-cyan-500/5 backdrop-blur-xl">
            <form onSubmit={handleSearchSubmit} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={t("adminTokens.searchAllPlaceholder")}
                  className="w-full rounded-2xl border border-slate-700 bg-slate-950/60 py-2.5 pl-9 pr-4 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
                />
              </div>
              <button
                type="submit"
                disabled={allLoading}
                className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-cyan-700/30 transition hover:opacity-90 disabled:opacity-50"
              >
                {allLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                {t("adminTokens.searchButton")}
              </button>
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
                  className="flex items-center gap-1.5 rounded-2xl border border-slate-700 bg-slate-800/60 px-4 py-2.5 text-sm text-slate-300 transition hover:bg-slate-700"
                >
                  <XCircle className="h-4 w-4" />
                  {t("adminTokens.clearFilter")}
                </button>
              )}
            </form>
          </div>

          <div className="rounded-3xl border border-white/10 bg-slate-900/70 shadow-xl shadow-cyan-500/5 backdrop-blur-xl overflow-hidden">
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <p className="text-sm text-slate-400">
                {pagination ? t("adminTokens.totalCount", { total: pagination.total }) : ""}
              </p>
              <button
                type="button"
                onClick={() => fetchAll(page, search)}
                disabled={allLoading}
                className="flex items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-slate-700 disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${allLoading ? "animate-spin" : ""}`} />
                {t("adminTokens.refresh")}
              </button>
            </div>

            <TokenTable
              tokens={allResult?.tokens}
              showUser
              loading={allLoading && !allResult}
            />

            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-between border-t border-white/10 px-6 py-4">
                <p className="text-xs text-slate-500">
                  {t("adminTokens.pageInfo", { page: pagination.page, pages: pagination.pages })}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || allLoading}
                    onClick={() => setPage((p) => p - 1)}
                    className="flex items-center gap-1 rounded-xl border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs text-slate-300 transition hover:bg-slate-700 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    {t("adminTokens.prevPage")}
                  </button>
                  <button
                    type="button"
                    disabled={page >= pagination.pages || allLoading}
                    onClick={() => setPage((p) => p + 1)}
                    className="flex items-center gap-1 rounded-xl border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs text-slate-300 transition hover:bg-slate-700 disabled:opacity-40"
                  >
                    {t("adminTokens.nextPage")}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* BY USER mode */}
      {mode === MODE_USER && (
        <>
          <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-cyan-500/5 backdrop-blur-xl">
            <h2 className="mb-4 text-base font-semibold text-cyan-100">
              {t("adminTokens.searchTitle")}
            </h2>
            <form onSubmit={handleUserSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("adminTokens.searchPlaceholder")}
                  className="w-full rounded-2xl border border-slate-700 bg-slate-950/60 py-2.5 pl-9 pr-4 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
                />
              </div>
              <button
                type="submit"
                disabled={userLoading}
                className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-cyan-700/30 transition hover:opacity-90 disabled:opacity-50"
              >
                {userLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                {t("adminTokens.searchButton")}
              </button>
            </form>
          </div>

          {userResult && (
            <div className="rounded-3xl border border-white/10 bg-slate-900/70 shadow-xl shadow-cyan-500/5 backdrop-blur-xl overflow-hidden">
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                <div className="flex items-center gap-3">
                  <Key className="h-5 w-5 text-cyan-400" />
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {userResult.user.firstName} {userResult.user.lastName}{" "}
                      <span className="font-mono text-cyan-300">
                        &lt;{userResult.user.email}&gt;
                      </span>
                    </p>
                    <p className="text-xs text-slate-400">
                      {t("adminTokens.tokenCount", {
                        total: userResult.tokens.length,
                        active: userResult.tokens.filter((tk) => tk.isActive).length,
                      })}
                    </p>
                  </div>
                </div>
                {!userResult.user.isActive && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-300">
                    <XCircle className="h-3 w-3" />
                    {t("adminTokens.accountDisabled")}
                  </span>
                )}
              </div>
              <TokenTable tokens={userResult.tokens} showUser={false} loading={false} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
